import type { Restaurant } from '~/types/api/data-contracts'
import { isAfterDate, parseDate } from '~/utils/dateTime'

/**
 * @description Check if the store is after closing time
 * @param closingTime {string} The closing time today
 * @param dateTime {string} The date to check the store status. Generated as current time if not provided
 * @returns {boolean} True if the store is after closing time
 */
export function checkStoreIsAfterClosingTime(closingTime: string, dateTime?: string): boolean {
  const currentDate = parseDate(dateTime ?? new Date())
  return isAfterDate(currentDate, new Date(closingTime))
}

export type RestaurantAvailability = 'open' | 'closed' | 'unavailable'

/** Trading hours take precedence over the online ordering flag. */
export function getRestaurantAvailability(restaurant: Restaurant, dateTime?: string): RestaurantAvailability {
  const now = parseDate(dateTime ?? new Date()).getTime()
  const opening = parseDate(restaurant.openingTimeToday).getTime()
  const closing = parseDate(restaurant.closingTimeToday).getTime()

  if (restaurant.hours[0]?.isClosed || !(now >= opening && now < closing)) {
    return 'closed'
  }

  return restaurant.isOnline ? 'open' : 'unavailable'
}

/** Whether the restaurant can accept online orders now. */
export function checkStoreIsOpen(restaurant: Restaurant, dateTime?: string): boolean {
  return getRestaurantAvailability(restaurant, dateTime) === 'open'
}

/** Shared wording for plain notices and notices with a restaurant-change action. */
export function getRestaurantOrderingNoticeContent(
  restaurant?: Restaurant,
  dateTime?: string
): { message: string; guidance: string } {
  if (restaurant && getRestaurantAvailability(restaurant, dateTime) === 'unavailable') {
    return {
      message: 'Online ordering is temporarily unavailable.',
      guidance: 'Check back soon'
    }
  }
  return {
    message: 'This restaurant is currently closed.',
    guidance: 'Check the opening hours'
  }
}

export function getRestaurantOrderingNotice(restaurant?: Restaurant, dateTime?: string): string {
  const { message, guidance } = getRestaurantOrderingNoticeContent(restaurant, dateTime)
  return `${message} ${guidance}.`
}

/**
 * @description Check if the store will open today
 * @param openingTime {string} The opening time today
 * @param dateTime {string} The date to check the store status. Generated as current time if not provided
 * @returns {boolean} True if the store will open today
 */
export function checkStoreWillOpenToday(openingTime: string, dateTime?: string): boolean {
  const currentDate = parseDate(dateTime ?? new Date())
  return currentDate < new Date(openingTime)
}

/**
 * @description Get the store online status message.
 * NOTE: The string returns '–' instead of '-'.
 * @param restaurant {Restaurant}
 * @param dateTime {string} The date to check the store status. Generated as current time if not provided
 * @returns {string} The store status in readable string
 */
export function getStoreOnlineStatusMessage(restaurant: Restaurant, dateTime?: string): string {
  const currentDayHours = restaurant.hours[0]

  const availability = getRestaurantAvailability(restaurant, dateTime)
  if (availability === 'unavailable') return 'Temporarily unavailable'

  if (availability === 'open') {
    const closingTime = currentDayHours?.description.split(' - ')[1]
    return closingTime ? `Open – Until ${closingTime}` : 'Open'
  }

  if (!currentDayHours?.isClosed && checkStoreWillOpenToday(restaurant.openingTimeToday, dateTime)) {
    const openingTime = currentDayHours?.description.split(' - ')[0]
    if (openingTime) return `Closed – Opens ${openingTime}`
  }

  // Outside trading hours, find the next open day
  for (let i = 1; i < restaurant.hours!.length; i++) {
    const candidateDay = restaurant.hours![i]
    if (!candidateDay.isClosed) {
      return `Closed – Open ${restaurant.hours![i].name}`
    }
  }

  // Return closed if no conditions are met
  return 'Closed'
}
